/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, inject } from '@angular/core';
import { ContentService } from '@alfresco/adf-content-services';
import { NotificationService } from '@alfresco/adf-core';
import { TranslateService } from '@ngx-translate/core';
import { WatermarkConfig, WatermarkService } from './watermark.service';

/**
 * What `print()` did with the request — so the caller can say whether the copy
 * that reached the printer carries the mark.
 */
export type WatermarkPrintOutcome = 'stamped' | 'plain' | 'unsupported';

/**
 * Stamps the administrator's watermark into the copy that goes to the printer.
 *
 * The on-screen mark is only a layer over the preview; a print opened straight
 * from the repository's content URL would carry nothing. So for the two
 * families the browser can safely rewrite — PDF and images — the file is
 * fetched, the mark is drawn into a copy, and that copy is what is printed.
 * The original is never modified.
 *
 * The mark itself is rendered once onto an offscreen canvas and reused for
 * every page: drawing text there means Thai (and every other script the
 * browser can shape) comes out exactly as it does on screen, without needing a
 * PDF font embedded. PDFs are rewritten with pdf-lib, loaded on demand so it
 * stays out of the main bundle.
 */
@Injectable({ providedIn: 'root' })
export class WatermarkPrintService {
  private readonly watermarks = inject(WatermarkService);
  private readonly content = inject(ContentService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  /**
   * Prints `nodeId` with the watermark when the configuration asks for it and
   * the format allows it. `plain` means the caller's ordinary print ran
   * instead; `unsupported` means it ran and the copy carries no mark — the
   * caller may want to say so.
   */
  async print(nodeId: string, mime: string, docName: string, printPlain: () => void): Promise<WatermarkPrintOutcome> {
    const cfg = this.watermarks.settings();
    if (!cfg.enabled || !cfg.print) {
      printPlain();
      return 'plain';
    }

    try {
      if (mime === 'application/pdf') {
        const url = await this.stampPdf(nodeId, cfg, docName);
        this.printUrl(url);
        return 'stamped';
      }
      if (mime?.startsWith('image/')) {
        const url = await this.stampImage(nodeId, cfg, docName);
        this.printImageUrl(url);
        return 'stamped';
      }
    } catch {
      // a copy that could not be stamped must not stop anyone printing
    }

    printPlain();
    this.notification.showInfo(this.translate.instant('DMS.WATERMARK.PRINT_UNSTAMPED'));
    return 'unsupported';
  }

  // ---- stamping ------------------------------------------------------------

  /** Downloads the file's bytes; the content URL already carries the ticket. */
  private async fetchBlob(nodeId: string): Promise<Blob> {
    const response = await fetch(this.content.getContentUrl(nodeId, false));
    if (!response.ok) {
      throw new Error(`content fetch failed: ${response.status}`);
    }
    return response.blob();
  }

  private async stampPdf(nodeId: string, cfg: WatermarkConfig, docName: string): Promise<string> {
    const { PDFDocument, degrees } = await import('pdf-lib');

    const source = await this.fetchBlob(nodeId);
    const doc = await PDFDocument.load(await source.arrayBuffer(), { ignoreEncryption: true });

    const tile = await this.renderTile(cfg, docName);
    const image = await doc.embedPng(tile.png);

    // CSS px on screen ≈ pt on paper at the usual 96/72 mapping
    const wPt = (tile.width * 72) / 96;
    const hPt = (tile.height * 72) / 96;
    // pdf-lib turns counter-clockwise; the on-screen mark turns clockwise
    const theta = (-cfg.rotation * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);

    // Where the (x, y) anchor must sit for the tile's centre to land on (cx, cy):
    // pdf-lib rotates the drawn rectangle about its bottom-left corner.
    const anchored = (cx: number, cy: number) => ({
      x: cx - (wPt / 2) * cos + (hPt / 2) * sin,
      y: cy - (wPt / 2) * sin - (hPt / 2) * cos
    });

    for (const page of doc.getPages()) {
      const { width, height } = page.getSize();

      if (cfg.layout === 'center') {
        const at = anchored(width / 2, height / 2);
        page.drawImage(image, { ...at, width: wPt, height: hPt, rotate: degrees(-cfg.rotation), opacity: cfg.opacity });
        continue;
      }

      // grid of centres, stepped wider than the tile so repeats read as a pattern
      const stepX = wPt + cfg.fontSize * 3;
      const stepY = hPt + cfg.fontSize * 2.5;
      for (let cy = -hPt; cy <= height + hPt; cy += stepY) {
        for (let cx = -wPt; cx <= width + wPt; cx += stepX) {
          const at = anchored(cx, cy);
          page.drawImage(image, { ...at, width: wPt, height: hPt, rotate: degrees(-cfg.rotation), opacity: cfg.opacity });
        }
      }
    }

    const bytes = await doc.save();
    return URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  }

  private async stampImage(nodeId: string, cfg: WatermarkConfig, docName: string): Promise<string> {
    const source = await this.fetchBlob(nodeId);
    const bitmap = await createImageBitmap(source);

    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('no 2d context');
    }
    ctx.drawImage(bitmap, 0, 0);

    const tile = await this.renderTile(cfg, docName);
    const theta = (cfg.rotation * Math.PI) / 180;

    const drawAt = (cx: number, cy: number) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(theta);
      ctx.globalAlpha = cfg.opacity;
      ctx.drawImage(tile.canvas, -tile.width / 2, -tile.height / 2);
      ctx.restore();
    };

    if (cfg.layout === 'center') {
      drawAt(canvas.width / 2, canvas.height / 2);
    } else {
      const stepX = tile.width + cfg.fontSize * 4;
      const stepY = tile.height + cfg.fontSize * 3.4;
      for (let cy = -tile.height; cy <= canvas.height + tile.height; cy += stepY) {
        for (let cx = -tile.width; cx <= canvas.width + tile.width; cx += stepX) {
          drawAt(cx, cy);
        }
      }
    }

    return canvas.toDataURL('image/png');
  }

  /**
   * The mark — logo above text — rendered once onto a transparent canvas at
   * 2× so it stays crisp on paper. Returned with its PNG bytes for pdf-lib and
   * the canvas itself for direct drawing.
   */
  private async renderTile(
    cfg: WatermarkConfig,
    docName: string
  ): Promise<{ canvas: HTMLCanvasElement; png: Uint8Array; width: number; height: number }> {
    const scale = 2;
    const text = this.watermarks.resolve(cfg.text, docName).trim();
    const font = `600 ${cfg.fontSize}px Inter, 'Noto Sans Thai', 'Leelawadee UI', Tahoma, 'Open Sans', sans-serif`;

    const measure = document.createElement('canvas').getContext('2d');
    if (!measure) {
      throw new Error('no 2d context');
    }
    measure.font = font;
    const textW = text ? measure.measureText(text).width : 0;

    const logo = cfg.logo ? await this.loadImage(cfg.logo) : null;
    const logoH = logo ? cfg.fontSize * 1.8 * (cfg.logoScale / 100) : 0;
    const logoW = logo ? (logoH * logo.naturalWidth) / logo.naturalHeight : 0;

    const gap = logo && text ? cfg.fontSize * 0.45 : 0;
    const width = Math.max(textW, logoW) || cfg.fontSize * 4;
    const height = logoH + gap + (text ? cfg.fontSize * 1.3 : 0);

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('no 2d context');
    }

    ctx.scale(scale, scale);
    ctx.fillStyle = cfg.color;
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    if (logo) {
      ctx.drawImage(logo, (width - logoW) / 2, 0, logoW, logoH);
    }
    if (text) {
      ctx.fillText(text, width / 2, logoH + gap);
    }

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      throw new Error('tile export failed');
    }

    return { canvas, png: new Uint8Array(await blob.arrayBuffer()), width, height };
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('logo could not be decoded'));
      img.src = src;
    });
  }

  // ---- opening the stamped copy -------------------------------------------

  /** A PDF blob in its own tab — the browser's viewer handles the printing. */
  private printUrl(url: string): void {
    const win = window.open(url, '_blank');
    if (win) {
      win.addEventListener('load', () => {
        try {
          win.focus();
          win.print();
        } catch {
          /* the tab still shows the stamped copy for manual printing */
        }
      });
    }
  }

  /** An image has no viewer of its own — give it a minimal page that prints. */
  private printImageUrl(dataUrl: string): void {
    const win = window.open('', '_blank');
    if (!win) {
      return;
    }
    win.document.write(
      `<!doctype html><title></title><body style="margin:0">` +
        `<img src="${dataUrl}" style="max-width:100%;display:block" ` +
        `onload="setTimeout(function(){window.focus();window.print()},60)">`
    );
    win.document.close();
  }
}
